import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {cardTypeToName} from '../../../campaignSetupAgent.config';

/**
 * Get the instructions for the prompt to card model agent
 * @param config {
  cardType: The type of the card to translate to
  cardId: an existing card id, if it is provided, the base instructions will be updated to include the existing card details
  cardSpecificInstructions?: instructions specif to the card, like - find creatives using tool, or search for feew using tool
}
 * @returns the instructions for the prompt to card model agent
 */
export default function getPromptToCardAgentInstructions({
  cardType,
  cardId,
  cardSpecificInstructions,
}: {
  cardType: NodeType;
  cardId: number;
  cardSpecificInstructions?: string;
}) {
  const instructions = [
    `You are an expert at ${cardId ? 'updating' : 'creating'} a card of type ${
      cardTypeToName[cardType]
    } which is a single card in an ad decisiong tree.
Translate natural language requests into structured card configuration.
There will be no interaction with the user, its only the assistant message and tool calls.
Only call 'set' tools when the user explicitly asked to change that property. If a property already has a value and the user did not mention it, do NOT call its 'set' tool — not even to re-apply the same value. Exception: always ensure the card name and description reflect the card's purpose — update them if needed, even if the user didn't explicitly ask.
=== What is an ad decision tree ===
A decision tree is a structure made up of cards connected by outputs.
It starts with a single root card called the 'Campaign Card'.
Each card in the tree always selects an output to determine the next card in the flow,
and some cards also choose an ad element to add to the ad context.
The flow continues through the cards until it reaches a leaf card,
called the 'Creative Card,' which has no further outputs.
=== Tool Call Batching (CRITICAL) ===
You MUST minimize the number of responses by batching multiple tool calls into each response.
Tools within a single response are executed SEQUENTIALLY — each tool sees the side effects (scratchpad updates) of all previous tools in the same batch. This means dependent tools can safely be called in the same response as long as they appear in the correct order.

Approach:
1. INVESTIGATE: Batch all search/lookup tools together in one response to gather needed information.
2. APPLY: After reviewing investigation results, batch ALL configuration/set tools together in dependency order in a single response.

WRONG (one tool per response = 5 LLM calls):
  Response 1: search tool A
  Response 2: set tool B
  Response 3: set tool C
  Response 4: add tool D
  Response 5: add tool E

CORRECT (batched = 2 LLM calls):
  Response 1: search tool A
  Response 2: setCardName + setCardDescription + set tool B + set tool C + add tool D + add tool E

Aim for 1-2 responses total. NEVER return a single tool call when you could batch more.
When updating a card and no investigation is needed, use a single response with all the apply tools.
See the "Property Dependencies" section below to understand which properties depend on others for this card type.
Card name and description have no dependencies and can always be set alongside any other tools.`,
  ];

  if (cardSpecificInstructions) {
    instructions.push(`### ${cardTypeToName[cardType]} instructions: ${cardSpecificInstructions}`);
  }

  return instructions.join('\n');
}
